import type {
  ComboboxData,
  ComboboxItem,
  ComboboxItemGroup,
} from "@mantine/core"
import { Group } from "@mantine/core"
import { IconTrash } from "@tabler/icons-react"
import { ActionIcon, AreYouSureModal, Select } from "@tfg/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { typeComponent } from "~/pages/Admin/Mapping/components/MappingTable"
import { useDeleteMappingElementMutation } from "~/pages/Admin/Mapping/hooks/useDeleteMappingElementMutation"
import { useUpdateMappingElementMutation } from "~/pages/Admin/Mapping/hooks/useUpdateMappingElementMutation"
import { MappingTypeNames, TypeKeys } from "~/pages/Admin/Mapping/types"
import { showAxiosError } from "~/utils/showAxiosError"

export type SelectProps = {
  typesList: TypeKeys[]
  mappingType: typeComponent
  typeName: MappingTypeNames
  page: number
}

export function filterDeletedAtSelectOptions(
  list: TypeKeys[] | undefined,
  id: number
): ComboboxData {
  if (!list) return []
  return list
    .filter((item) => !item.deletedAt || item.id === id)
    .map((list) => ({
      value: list.id.toString(),
      label: list.key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function MappingSelect({
  typesList,
  mappingType,
  typeName,
  page = 1,
}: SelectProps) {
  const { t } = useTranslation("admin")
  const { mutate: updateMappingType, isPending: isUpdatingMappingType } =
    useUpdateMappingElementMutation(
      { page },
      {
        onError(error) {
          showAxiosError({
            error: error as Error,
            showNotification: true,
          })
        },
      }
    )
  const { mutate: deleteMappingType, isPending: isDeletingMappingType } =
    useDeleteMappingElementMutation(
      {
        page,
      },
      {
        onError(error) {
          showAxiosError({
            error: error as Error,
            showNotification: true,
          })
        },
      }
    )

  const [value, setValue] = useState<string | null>(
    mappingType.type.typeId ? mappingType.type.typeId.toString() : null
  )
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [pendingChange, setPendingChange] = useState<{
    newValue: string
    newLabel: string
  } | null>(null)

  const options = useMemo(
    () => filterDeletedAtSelectOptions(typesList, Number(value)),
    [typesList, value]
  )

  const isComboboxItem = (
    item: string | ComboboxItem | ComboboxItemGroup
  ): item is ComboboxItem => {
    return (
      typeof item !== "string" &&
      "value" in item &&
      "label" in item &&
      !("group" in item)
    )
  }

  const getCurrentLabel = (): string => {
    if (!value) return t("mapping.none")
    const option = options.find((opt) => {
      if (!isComboboxItem(opt)) return false
      return opt.value === value
    })
    if (!option || !isComboboxItem(option)) return t("mapping.none")
    return option.label
  }

  const handleSelectChange = (newValue: string | null): void => {
    if (!newValue) return

    const newOption = options.find((opt) => {
      if (!isComboboxItem(opt)) return false
      return opt.value === newValue
    })

    if (newOption && isComboboxItem(newOption)) {
      setPendingChange({
        newValue,
        newLabel: newOption.label,
      })
      setIsUpdateModalOpen(true)
    }
  }

  const handleConfirmChange = () => {
    if (!pendingChange) return

    setValue(pendingChange.newValue)
    updateMappingType({
      data: {
        alias: mappingType.type.alias,
        type: typeName,
        typeId: Number(pendingChange.newValue),
      },
    })
    setPendingChange(null)
  }

  const handleCancelChange = () => {
    setPendingChange(null)
  }

  const handleConfirmDelete = () => {
    deleteMappingType({
      data: { alias: mappingType.type.alias, type: typeName },
    })
  }

  return (
    <Group justify="space-between" wrap="nowrap" w="100%">
      <Select
        data={options}
        inlineLabel
        size="xs"
        value={value}
        isLoading={isUpdatingMappingType}
        onChange={handleSelectChange}
        searchable
        label={mappingType.type.alias}
        disabled={isUpdatingMappingType || isDeletingMappingType}
      />
      <ActionIcon
        icon={<IconTrash />}
        disabled={isDeletingMappingType || isUpdatingMappingType}
        variant="secondary-critical"
        onClick={() => setIsDeleteModalOpen(true)}
      />
      <AreYouSureModal
        opened={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false)
          handleCancelChange()
        }}
        onConfirm={handleConfirmChange}
        onCancel={handleCancelChange}
        title={t("mapping.modal.update.title")}
        areYouSureText={t("mapping.modal.update.areYouSureText", {
          alias: mappingType.type.alias,
          label: getCurrentLabel(),
          newLabel: pendingChange?.newLabel ?? "",
        })}
        confirmText={t("mapping.modal.update.confirmText")}
        cancelText={t("mapping.modal.update.cancelText")}
      />
      <AreYouSureModal
        opened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t("mapping.modal.delete.title")}
        areYouSureText={t("mapping.modal.delete.areYouSureText", {
          alias: mappingType.type.alias,
          label: getCurrentLabel(),
        })}
        confirmText={t("mapping.modal.delete.confirmText")}
        cancelText={t("mapping.modal.delete.cancelText")}
      />
    </Group>
  )
}
